import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Activity, AlertTriangle, CheckCircle2, FileCode, Folder,
  TrendingDown, Zap, RefreshCw, Loader2,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const ENDPOINT = '/api/admin/refactor-health';

const tone = (lines) => {
  if (lines >= 1500) return { label: 'Critical', cls: 'bg-rose-500 text-white' };
  if (lines >= 1000) return { label: 'Hot Spot', cls: 'bg-amber-500 text-white' };
  if (lines >= 500) return { label: 'Watch', cls: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-300' };
  return { label: 'Healthy', cls: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300' };
};

const ScoreBadge = ({ score }) => {
  const color =
    score >= 75 ? 'text-emerald-600 dark:text-emerald-400' :
    score >= 50 ? 'text-amber-600 dark:text-amber-400' :
    'text-rose-600 dark:text-rose-400';
  return (
    <div className="flex items-baseline gap-1">
      <span className={cn('text-5xl font-bold tracking-tight', color)} data-testid="refactor-health-score">{score}</span>
      <span className="text-slate-400 text-2xl">/100</span>
    </div>
  );
};

const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700">
    <div className={cn('p-2 rounded-md', accent || 'bg-slate-200 dark:bg-slate-700')}>
      <Icon className="w-4 h-4 text-slate-700 dark:text-slate-200" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
    </div>
  </div>
);

const FileRow = ({ name, lines, endpoints, baseline }) => {
  const t = tone(lines);
  const reduction = baseline
    ? Math.round((1 - lines / baseline) * 100)
    : null;
  return (
    <div
      className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded-md text-sm"
      data-testid={`refactor-row-${name}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FileCode className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="font-mono text-slate-700 dark:text-slate-200 truncate">{name}</span>
        <Badge className={cn('text-[10px] px-1.5 py-0', t.cls)}>{t.label}</Badge>
      </div>
      <div className="flex items-center gap-4 shrink-0 text-xs text-slate-500 dark:text-slate-400">
        {typeof endpoints === 'number' && (
          <span><b className="text-slate-700 dark:text-slate-200">{endpoints}</b> ep</span>
        )}
        <span><b className="text-slate-700 dark:text-slate-200">{lines.toLocaleString()}</b> lines</span>
        {reduction !== null && reduction > 0 && (
          <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <TrendingDown className="w-3 h-3" />
            {reduction}%
          </span>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children, count }) => (
  <Card>
    <CardHeader className="py-3">
      <CardTitle className="flex items-center justify-between text-sm font-semibold">
        <span className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          {title}
        </span>
        {typeof count === 'number' && (
          <Badge variant="outline" className="text-xs">{count}</Badge>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0 px-3 pb-3 space-y-0.5">
      {children}
    </CardContent>
  </Card>
);

const RefactorHealthPanel = ({ token, backendUrl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}${ENDPOINT}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  if (loading) {
    return (
      <Card data-testid="refactor-health-loading">
        <CardContent className="p-12 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Inspecting codebase…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="refactor-health-error">
        <CardContent className="p-6 flex items-center gap-3 text-rose-600 dark:text-rose-400">
          <AlertTriangle className="w-5 h-5" />
          Couldn't load refactor health: {error}
          <Button onClick={load} variant="outline" size="sm" className="ml-auto">
            <RefreshCw className="w-3 h-3 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { score, modular_pct, backend, frontend, hot_spots } = data;

  return (
    <div className="space-y-6" data-testid="refactor-health-panel">
      {/* Hero */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-violet-600" />
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Refactor Health
                </span>
              </div>
              <ScoreBadge score={score} />
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md">
                {modular_pct}% of backend endpoints live in modular routers.
                {hot_spots.length > 0
                  ? ` ${hot_spots.length} hot spot${hot_spots.length === 1 ? '' : 's'} need attention.`
                  : ' No files >1000 lines — clean.'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 min-w-[320px]">
              <Stat
                icon={FileCode}
                label="server.py"
                value={`${backend.server_py.lines.toLocaleString()} lines`}
                sub={`${backend.server_py.reduction_pct}% smaller than baseline (${backend.server_py.baseline_lines.toLocaleString()})`}
                accent="bg-blue-100 dark:bg-blue-900/30"
              />
              <Stat
                icon={Folder}
                label="Modular Routers"
                value={`${backend.routers.length} files`}
                sub={`${backend.modular_endpoints} endpoints`}
                accent="bg-emerald-100 dark:bg-emerald-900/30"
              />
              <Stat
                icon={Zap}
                label="Total Endpoints"
                value={backend.total_endpoints}
                sub={`${backend.server_py.endpoints} monolith / ${backend.modular_endpoints} modular`}
                accent="bg-violet-100 dark:bg-violet-900/30"
              />
              <Stat
                icon={hot_spots.length > 0 ? AlertTriangle : CheckCircle2}
                label="Hot Spots"
                value={hot_spots.length}
                sub="Files > 1000 lines"
                accent={hot_spots.length > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={load} variant="outline" size="sm" data-testid="refactor-health-refresh">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Backend monolith vs modular */}
        <Section title="Backend Monolith" icon={FileCode}>
          <FileRow
            name="server.py"
            lines={backend.server_py.lines}
            endpoints={backend.server_py.endpoints}
            baseline={backend.server_py.baseline_lines}
          />
        </Section>

        <Section title="Backend Routers" icon={Folder} count={backend.routers.length}>
          {backend.routers.map((r) => (
            <FileRow
              key={r.name}
              name={r.name}
              lines={r.lines}
              endpoints={r.endpoints}
            />
          ))}
        </Section>

        {/* Frontend pages */}
        <Section title="Frontend Pages" icon={FileCode} count={frontend.pages.length}>
          {frontend.pages
            .slice()
            .sort((a, b) => b.lines - a.lines)
            .slice(0, 12)
            .map((p) => (
              <FileRow key={p.name} name={p.name} lines={p.lines} />
            ))}
        </Section>

        {/* Frontend components */}
        <Section title="Frontend Component Folders" icon={Folder} count={frontend.component_subdirs.length}>
          {frontend.component_subdirs.map((sub) => (
            <div key={sub.name} className="px-2 py-1.5">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                <Folder className="w-3.5 h-3.5 text-slate-400" />
                {sub.name}/ <span className="text-xs text-slate-400">({sub.files.length} files)</span>
              </div>
              <div className="ml-5 space-y-0.5">
                {sub.files.map((f) => (
                  <FileRow key={f.name} name={f.name} lines={f.lines} />
                ))}
              </div>
            </div>
          ))}
        </Section>
      </div>

      {/* Hot spots */}
      {hot_spots.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-900/50">
          <CardHeader className="py-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Hot Spots — files exceeding 1,000 lines
              <Badge className="bg-amber-500 text-white">{hot_spots.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-3 pb-3 space-y-0.5">
            {hot_spots.map((h) => (
              <FileRow key={h.file} name={h.file} lines={h.lines} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { RefactorHealthPanel };
export default RefactorHealthPanel;
